import apiMiddleware from "../api/apiMiddleware";

// Rental Item CRUD
export const getRentalItems = async (page = 1, filters = {}) => {
    const queryParams = new URLSearchParams({
        page,
        ...filters,
    }).toString();
    const response = await apiMiddleware.get(`/supplier/rental/items?${queryParams}`);
    return response.data;
};

export const createRentalItem = async (data) => {
    const response = await apiMiddleware.post("/supplier/rental/items", data);
    return response.data;
};

export const updateRentalItem = async (id, data) => {
    // If data is FormData, we must use POST with _method=PUT for Laravel to see the files
    if (data instanceof FormData) {
        data.append("_method", "PUT");
        const response = await apiMiddleware.post(`/supplier/rental/items/${id}`, data, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
    }
    const response = await apiMiddleware.put(`/supplier/rental/items/${id}`, data);
    return response.data;
};

export const storeRentalItemPricing = async (id, data = {}) => {
    const response = await apiMiddleware.post(`/supplier/rental/items/${id}/pricing`, data);
    return response.data;
};

export const updateRentalItemPricing = async (id, data) => {
    const response = await apiMiddleware.put(`/supplier/rental/items/${id}/pricing`, data);
    return response.data;
};

// Weekday pricing
export const listRentalWeekdayPrices = async (rentalItemId) => {
    const response = await apiMiddleware.get(`/supplier/rental/items/${rentalItemId}/weekday-prices`);
    return response.data;
};

export const upsertRentalWeekdayPrice = async (rentalItemId, data) => {
    const response = await apiMiddleware.post(`/supplier/rental/items/${rentalItemId}/weekday-prices`, data);
    return response.data;
};

export const deleteRentalWeekdayPrice = async (weekdayPriceId) => {
    const response = await apiMiddleware.delete(`/supplier/rental/weekday-prices/${weekdayPriceId}`);
    return response.data;
};

export const uploadRentalGalleryImages = async (rentalItemId, formData) => {
    const response = await apiMiddleware.post(`/supplier/rental/items/${rentalItemId}/gallery/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
};

export const deleteRentalGalleryImage = async (imageId) => {
    const response = await apiMiddleware.delete(`/supplier/rental/gallery-images/${imageId}`);
    return response.data;
};

export const setRentalPrimaryImage = async (imageId) => {
    const response = await apiMiddleware.post(`/supplier/rental/gallery-images/${imageId}/set-primary`);
    return response.data;
};

export const deleteRentalItem = async (id) => {
    const response = await apiMiddleware.delete(`/supplier/rental/items/${id}`);
    return response.data;
};

export const getRentalItemById = async (id) => {
    const response = await apiMiddleware.get(`/supplier/rental/items/${id}`);
    return response.data;
};

// Rental Categories
export const getRentalCategories = async () => {
    const response = await apiMiddleware.get("/supplier/rental/categories");
    return response.data;
};

export const createRentalCategory = async (data) => {
    const isFormData = data instanceof FormData;
    const response = await apiMiddleware.post("/supplier/rental/categories", data, isFormData
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : undefined);
    return response.data;
};

export const updateRentalCategory = async (id, data) => {
    const isFormData = data instanceof FormData;
    if (isFormData) {
        data.append("_method", "PUT");
        const response = await apiMiddleware.post(`/supplier/rental/categories/${id}`, data, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
    }
    const response = await apiMiddleware.put(`/supplier/rental/categories/${id}`, data);
    return response.data;
};

export const deleteRentalCategory = async (id) => {
    const response = await apiMiddleware.delete(`/supplier/rental/categories/${id}`);
    return response.data;
};

export const deleteRentalCategoryImage = async (id) => {
    const response = await apiMiddleware.delete(`/supplier/rental/categories/${id}/image`);
    return response.data;
};

export const getRentalCategoriesMaster = async () => {
    const response = await apiMiddleware.get("/supplier/rental/categories-master");
    return response.data;
};

export const getRentalSubCategoriesMaster = async (categoryId) => {
    const response = await apiMiddleware.get(`/supplier/rental/sub-categories-master/${categoryId}`);
    return response.data;
};

// Rental Features (master)
export const getRentalFeaturesMaster = async (formType) => {
    const q =
        formType !== undefined && formType !== null && formType !== ""
            ? `?form_type=${encodeURIComponent(formType)}`
            : "";
    const response = await apiMiddleware.get(`/supplier/rental/features-master${q}`);
    return response.data;
};

export const createRentalFeature = async (data) => {
    const response = await apiMiddleware.post("/supplier/rental/features", data);
    return response.data;
};

export const getRentalSubCategories = async (rentalCategoryId) => {
    const q =
        rentalCategoryId !== undefined && rentalCategoryId !== null && rentalCategoryId !== ""
            ? `?rental_category_id=${encodeURIComponent(rentalCategoryId)}`
            : "";
    const response = await apiMiddleware.get(`/supplier/rental/sub-categories${q}`);
    return response.data;
};

export const createRentalSubCategory = async (data) => {
    const response = await apiMiddleware.post("/supplier/rental/sub-categories", data);
    return response.data;
};

export const updateRentalSubCategory = async (id, data) => {
    const response = await apiMiddleware.put(`/supplier/rental/sub-categories/${id}`, data);
    return response.data;
};

export const deleteRentalSubCategory = async (id) => {
    const response = await apiMiddleware.delete(`/supplier/rental/sub-categories/${id}`);
    return response.data;
};

// Bookings
export const getRentalBookings = async () => {
    const response = await apiMiddleware.get("/supplier/rental/bookings");
    return response.data;
};

export const updateRentalBookingStatus = async (id, data) => {
    const response = await apiMiddleware.patch(`/supplier/rental/bookings/${id}/status`, data);
    return response.data;
};

export const refundRentalBookingAmount = async (id, data) => {
    const response = await apiMiddleware.post(`/supplier/rental/bookings/${id}/refund`, data);
    return response.data;
};

// Blocked Dates
export const getRentalBlockedDates = async (itemId) => {
    const response = await apiMiddleware.get(`/supplier/rental/items/${itemId}/blocked-dates`);
    return response.data;
};

export const createRentalBlockedDate = async (data) => {
    const response = await apiMiddleware.post("/supplier/rental/blocked-dates", data);
    return response.data;
};

export const deleteRentalBlockedDate = async (id) => {
    const response = await apiMiddleware.delete(`/supplier/rental/blocked-dates/${id}`);
    return response.data;
};

// Terms and Conditions
export const getRentalTerms = async (itemId) => {
    const response = await apiMiddleware.get(`/supplier/rental/items/${itemId}/terms`);
    return response.data;
};

export const createRentalTerm = async (data) => {
    const response = await apiMiddleware.post("/supplier/rental/terms", data);
    return response.data;
};

export const deleteRentalTerm = async (id) => {
    const response = await apiMiddleware.delete(`/supplier/rental/terms/${id}`);
    return response.data;
};

// FAQs
export const getRentalFAQs = async (itemId) => {
    const response = await apiMiddleware.get(`/supplier/rental/items/${itemId}/faqs`);
    return response.data;
};

export const createRentalFAQ = async (data) => {
    const response = await apiMiddleware.post("/supplier/rental/faqs", data);
    return response.data;
};

export const updateRentalFAQ = async (id, data) => {
    const response = await apiMiddleware.put(`/supplier/rental/faqs/${id}`, data);
    return response.data;
};

export const deleteRentalFAQ = async (id) => {
    const response = await apiMiddleware.delete(`/supplier/rental/faqs/${id}`);
    return response.data;
};

// Dashboard Analytics
export const getRentalAnalytics = async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await apiMiddleware.get(`/supplier/dashboard/rental-analytics?${queryParams}`);
    return response.data;
};
